import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export const ONLINE_CONFIG_CDN = 'https://cdn.hotel3d.ru';
export const DIRECT_SUPABASE_URL = 'https://ikciksykpncxrhjbbnfg.supabase.co';
export const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlrY2lrc3lrcG5jeHJoamJibmZnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2MTU3MTcsImV4cCI6MjA2MzE5MTcxN30._jDyk-LtrRYOKdn6zddJr0LLpsUGKqe9RJdkZQUI4Ak';

export interface OnlineWidgetConfig {
  hotelId: string;
  slug: string;
  useProxy?: boolean;
  proxyUrl: string;
  fallbackUrl?: string;
  anonKey?: string;
  version?: string;
  branding?: {
    hotelName?: string;
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    posterUrl?: string;
    videoUrl?: string;
    heroOverlay?: number;
    bookingTitle?: string | null;
    bookingSubtitle?: string | null;
    roomsTitle?: string | null;
    roomsSubtitle?: string | null;
  };
  contacts?: {
    whatsappPhone?: string;
    phone?: string;
  };
  seo?: {
    metaTitle?: string | null;
    metaDescription?: string | null;
    ogImage?: string | null;
    faviconUrl?: string | null;
    canonicalUrl?: string | null;
  };
  display?: {
    defaultCheckin?: string | null;
    hideChildPolicy?: boolean | null;
    hideMealSurcharge?: boolean | null;
    emailRequired?: boolean | null;
  };
}

declare global {
  interface Window {
    __HOTEL3D_WIDGET_CONFIG__?: OnlineWidgetConfig;
    __HOTEL3D_ONLINE_WIDGET_DIAG__?: Record<string, unknown>;
  }
}

let activeOnlineConfig: OnlineWidgetConfig | null = null;
let activeOnlineBootstrap: Record<string, unknown> | null = null;
const onlineClientCache = new Map<string, SupabaseClient<Database>>();

export const setActiveOnlineWidgetConfig = (config: OnlineWidgetConfig | null) => {
  activeOnlineConfig = config;
};

export const getActiveOnlineWidgetConfig = () => activeOnlineConfig;

export const setActiveOnlineWidgetBootstrap = (bootstrap: Record<string, unknown> | null) => {
  activeOnlineBootstrap = bootstrap;
  if (typeof window !== 'undefined') {
    const childPolicies = bootstrap?.childPolicies;
    const childPoliciesCount = childPolicies && typeof childPolicies === 'object' && !Array.isArray(childPolicies)
      ? Object.keys(childPolicies).length
      : 0;
    (window as any).__HOTEL3D_ONLINE_WIDGET_DIAG__ = {
      ...((window as any).__HOTEL3D_ONLINE_WIDGET_DIAG__ || {}),
      hasChildPolicies: childPoliciesCount > 0,
      childPoliciesCount,
    };
    window.dispatchEvent(new CustomEvent('hotel3d-online-bootstrap', { detail: bootstrap }));
  }
};

export const getActiveOnlineWidgetBootstrap = () => activeOnlineBootstrap;

export const getEmbeddedOnlineWidgetConfig = (): OnlineWidgetConfig | null => {
  if (typeof window === 'undefined') return null;
  const config = window.__HOTEL3D_WIDGET_CONFIG__;
  return config?.hotelId && config?.proxyUrl ? config : null;
};

export const getUrlOnlineWidgetConfig = (): OnlineWidgetConfig | null => {
  if (typeof window === 'undefined') return null;
  const encoded = new URLSearchParams(window.location.search).get('bootstrap');
  if (!encoded) return null;
  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const config = JSON.parse(new TextDecoder().decode(bytes)) as OnlineWidgetConfig;
    return config?.hotelId && config?.proxyUrl ? config : null;
  } catch {
    return null;
  }
};

export const getInitialOnlineWidgetConfig = (): OnlineWidgetConfig | null => (
  getEmbeddedOnlineWidgetConfig() || getUrlOnlineWidgetConfig()
);

export const isOnlineWidgetRuntime = () => (
  typeof window !== 'undefined'
  && (window.location.pathname.includes('online.html') || window.location.pathname.includes('widget.html'))
);

export const getOnlineSlug = () => {
  const initialConfig = getInitialOnlineWidgetConfig();
  if (initialConfig?.slug) return initialConfig.slug.trim();

  const params = new URLSearchParams(window.location.search);
  const querySlug = params.get('hotel') || params.get('slug') || params.get('booking');
  if (querySlug?.trim()) return querySlug.trim();

  const hash = window.location.hash || '';
  const match = hash.match(/\/booking\/([^?/#]+)/);
  return match?.[1]?.trim() || '';
};

/** 🆕 Параметры предзаполнения формы, переданные сайтом отеля через URL */
export interface WidgetUrlParams {
  checkIn?: Date;
  checkOut?: Date;
  adults?: number;
  children?: number;
  childrenAges?: number[];
  infants?: number;
  ratePlanId?: string;
  categoryId?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const parseIsoDate = (raw: string | null): Date | undefined => {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return undefined;
  const [y, m, d] = raw.trim().split('-').map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return undefined;
  return date;
};

const parseIntInRange = (raw: string | null, min: number, max: number): number | undefined => {
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined;
  return Math.min(max, Math.max(min, n));
};

/**
 * 🎯 АК-47: читает параметры предзаполнения из URL.
 * Поддерживает и обычную query-строку (до `#`), и query-часть хеша:
 *   /online.html?checkin=...#/booking/slug
 *   /online.html#/booking/slug?checkin=...
 * Любой некорректный параметр молча игнорируется — поведение по умолчанию сохраняется.
 */
export const parseWidgetUrlParams = (): WidgetUrlParams => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash || '';
  const hashQueryIndex = hash.indexOf('?');
  if (hashQueryIndex >= 0) {
    const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
    hashParams.forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }

  const result: WidgetUrlParams = {};

  // Даты — только валидная пара в будущем (или сегодня)
  const checkIn = parseIsoDate(params.get('checkin') || params.get('check_in'));
  const checkOut = parseIsoDate(params.get('checkout') || params.get('check_out'));
  if (checkIn && checkOut && checkOut.getTime() > checkIn.getTime()) {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    if (checkIn.getTime() >= todayStart.getTime()) {
      result.checkIn = checkIn;
      result.checkOut = checkOut;
    }
  }

  const adults = parseIntInRange(params.get('adults'), 1, 10);
  if (adults !== undefined) result.adults = adults;

  const children = parseIntInRange(params.get('children'), 0, 10);
  if (children !== undefined) result.children = children;

  const infants = parseIntInRange(params.get('infants'), 0, 10);
  if (infants !== undefined) result.infants = infants;

  const rawAges = params.get('childAges') || params.get('child_ages');
  if (rawAges) {
    const ages = rawAges
      .split(',')
      .map(part => parseIntInRange(part, 0, 17))
      .filter((n): n is number => n !== undefined);
    if (ages.length > 0) result.childrenAges = ages.slice(0, result.children ?? ages.length);
  }

  const rate = (params.get('rate') || params.get('rate_plan') || '').trim();
  if (UUID_RE.test(rate)) result.ratePlanId = rate;

  const category = (params.get('category') || params.get('category_id') || '').trim();
  if (UUID_RE.test(category)) result.categoryId = category;

  return result;
};


export const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    if (label === 'fetch') return await promise;
    return await promise;
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchOnlineConfigOnce = async (slug: string, timeoutMs: number): Promise<OnlineWidgetConfig> => {
  const cacheBust = Date.now().toString(36);
  const url = `${ONLINE_CONFIG_CDN}/config/${encodeURIComponent(slug)}.json?v=${cacheBust}`;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error(`config ${response.status}`);
    const config = await response.json();
    if (!config?.hotelId || !config?.proxyUrl) throw new Error('Некорректный config JSON');
    return config as OnlineWidgetConfig;
  } finally {
    window.clearTimeout(timer);
  }
};

export const fetchOnlineConfig = async (slug: string, timeoutMs = 8000): Promise<OnlineWidgetConfig> => {
  try {
    return await fetchOnlineConfigOnce(slug, timeoutMs);
  } catch {
    // Единственный ретрай с увеличенным окном: защита от холодного старта CDN/сети
    return await fetchOnlineConfigOnce(slug, Math.max(timeoutMs, 12000));
  }
};


export const createOnlineSupabaseClient = (baseUrl: string, anonKey?: string): SupabaseClient<Database> => {
  const normalizedUrl = baseUrl.replace(/\/$/, '');
  const cacheKey = `${normalizedUrl}:${anonKey || SUPABASE_ANON_KEY}`;
  const cached = onlineClientCache.get(cacheKey);
  if (cached) return cached;

  const client = createClient<Database>(normalizedUrl, anonKey || SUPABASE_ANON_KEY, {
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        headers.delete('content-profile');
        headers.delete('Content-Profile');
        headers.delete('accept-profile');
        headers.delete('Accept-Profile');

        return fetch(input, { ...init, headers });
      },
    },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'hotel3d-online-widget-no-auth',
    },
    realtime: {
      params: { eventsPerSecond: 0 },
    },
  });
  onlineClientCache.set(cacheKey, client);
  return client;
};

export const unregisterOnlineServiceWorkers = () => {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistrations()
    .then((registrations) => registrations.forEach((registration) => registration.unregister()))
    .catch(() => undefined);
};
/**
 * 🎯 АК-47: живая подстраховка настроек витрины.
 * Опубликованный config.json может быть устаревшим (сгенерирован до появления флага),
 * поэтому после загрузки конфига мы дочитываем актуальные настройки отображения
 * напрямую из базы. Любая ошибка молча игнорируется — виджет продолжает работать на конфиге.
 */
export const fetchLiveDisplaySettings = async (
  client: SupabaseClient<Database>,
  hotelId: string
): Promise<OnlineWidgetConfig['display'] | null> => {
  try {
    const { data, error } = await client
      .from('hotels')
      .select('widget_hide_child_policy, widget_hide_meal_surcharge, widget_email_required')
      .eq('id', hotelId)
      .maybeSingle();

    if (error || !data) return null;

    return {
      hideChildPolicy: data.widget_hide_child_policy ?? null,
      hideMealSurcharge: data.widget_hide_meal_surcharge ?? false,
      emailRequired: data.widget_email_required ?? true,
    };
  } catch {
    return null;
  }
};
